import { Box } from "@mui/material";
import { useMemo } from "react";

import { Option } from "api/common/types/common.types";
import { Ecr } from "api/memberPortal/ecr/ecr.types";
import { AdminPolicyLetter } from "api/memberPortal/policies/policies.types";
import { PublicLabgram } from "api/public/labgrams/labgrams.types";
import { withForm } from "components/Forms/AppForm";

import { defaultProficiencyFeedbackValues } from "../../../ProficiencyFeedbackForm.default";
import { ProficiencyFeedbackFormField } from "../../../ProficiencyFeedbackForm.schema";
import { PF_FORM_YES_NO_QUESTIONS } from "../data/PfQuestions";

/** Shape of the nested conditional field groups when accessed dynamically */
type ConditionalFieldGroup = Record<
  string,
  boolean | string | { label: string; value: string }[] | null | undefined
>;

const QuestionsForm = withForm({
  defaultValues: defaultProficiencyFeedbackValues,
  props: {
    ecrs: [] as Ecr[],
    readonly: false,
    policies: [] as AdminPolicyLetter[],
    labgrams: [] as PublicLabgram[],
  },
  render: function Render({ form, ecrs, readonly, policies, labgrams }) {
    const options = useMemo(() => {
      const ecrOptions = ecrs.map(({ ecrid, gist }) => ({
        value: `${ecrid}`,
        label: `${ecrid} - ${gist}`,
      }));

      const policiesOptions = policies
        .filter(({ archived }) => !archived)
        .sort((a, b) => {
          if (!a.policy_num) return -1;
          if (!b.policy_num) return 1;

          const policyCompare = a.policy_num - b.policy_num;

          if (policyCompare === 0) {
            if (!a.addendum_num) return -1;
            if (!b.addendum_num) return 1;
            return a.addendum_num - b.addendum_num;
          }

          return policyCompare;
        })
        .map(({ policy_num, policy_title, addendum_num }) => ({
          value: `Policy ${policy_num} ${addendum_num ? `[Addendum ${addendum_num}]` : ""}`,
          label: `Policy ${policy_num} ${addendum_num ? `[Addendum ${addendum_num}]` : ""} - ${policy_title}`,
        }));

      const labgramOptions = labgrams
        .filter(({ archived }) => !archived)
        .map(({ labgram_id, subject }) => ({
          value: `Labgram ${labgram_id}`,
          label: `Labgram ${labgram_id} ${subject}`,
        }));

      const combinedPolicies = [...policiesOptions, ...labgramOptions];

      return {
        missTdsEcrs: ecrOptions,
        misinterpretedPolicies: combinedPolicies,
        performTestsEcrs: ecrOptions,
      } as const;
    }, [ecrs, policies, labgrams]);

    return PF_FORM_YES_NO_QUESTIONS.map(
      ({
        label,
        group,
        parentField,
        extraField,
        extraFieldLabel,
        extraFieldType,
        showOn,
      }) => (
        <Box key={`${group}.${parentField}`} mt={3}>
          <form.AppField
            name={`${group}.${parentField}` as ProficiencyFeedbackFormField}
            listeners={{
              onChange: () => {
                const defaults = defaultProficiencyFeedbackValues[
                  group
                ] as ConditionalFieldGroup;
                form.setFieldValue(
                  `${group}.${extraField}` as ProficiencyFeedbackFormField,
                  defaults?.[extraField],
                );
              },
            }}
          >
            {(field) => (
              <field.YesNoRadioButton
                label={label}
                isHorizontal
                required={true}
                disabled={readonly}
              />
            )}
          </form.AppField>

          <form.Subscribe
            selector={(state) =>
              (
                state.values[
                  group as keyof typeof state.values
                ] as ConditionalFieldGroup
              )?.[parentField]
            }
          >
            {(radioValue) =>
              radioValue === showOn && (
                <Box mt={1}>
                  <form.AppField
                    name={
                      `${group}.${extraField}` as ProficiencyFeedbackFormField
                    }
                  >
                    {(field) =>
                      extraFieldType === "text" ? (
                        <field.TextInput
                          label={extraFieldLabel}
                          required
                          disabled={readonly}
                        />
                      ) : (
                        <field.MultiAutocompleteSelect
                          label={extraFieldLabel}
                          options={
                            options[extraField as keyof typeof options] ?? []
                          }
                          required
                          disabled={readonly}
                          getChipLabel={(opt: Option) => opt.value.toString()}
                          groupBy={(opt: Option) => {
                            const firstWord = opt.label.split(" ")[0];
                            const groupFun =
                              groupFuns[firstWord as keyof typeof groupFuns] ??
                              groupFuns.Ecrs;
                            return groupFun(opt);
                          }}
                        />
                      )
                    }
                  </form.AppField>
                </Box>
              )
            }
          </form.Subscribe>
        </Box>
      ),
    );
  },
});

export default QuestionsForm;

const groupPolicies = (opt: Option) => `Policy ${opt.label.split(" ")[1]}`;
const groupLabgrams = (_opt: Option) => `Labgrams`;
const groupEcrs = (_opt: Option) => `ECR`;

const groupFuns = {
  Policy: groupPolicies,
  Labgram: groupLabgrams,
  Ecrs: groupEcrs,
} as const;
